import { useCallback } from "react";
import isArray from "lodash/isArray";
import isObject from "lodash/isObject";

const useAssociationUtils = () => {
  const getTotalArrayItems = useCallback((obj) => {
    try {
      if (!obj || typeof obj !== "object") return 0;

      return Object.values(obj).reduce((total, value) => {
        if (Array.isArray(value)) return total + value.length;
        return total;
      }, 0);
    } catch (error) {
      return 0;
    }
  }, []);

  const getLoanDocumentAssociationSummary = useCallback(
    (loanData, loanDocumentId, options = {}) => {
      try {
        const returnType = options.returnType === "objects" ? "objects" : "ids";

        if (!loanDocumentId || !isObject(loanData)) {
          return { totalCount: 0, categorized: {} };
        }

        const categorized = {};
        let totalCount = 0;

        const pushResult = (groupKey, item) => {
          const val = returnType === "objects" ? item : item?._id;
          if (!val) return;

          if (!categorized[groupKey]) categorized[groupKey] = [];
          categorized[groupKey].push(val);
          totalCount += 1;
        };

        const scanAssociatableArray = (groupKey, arr) => {
          if (!isArray(arr)) return;

          arr.forEach((item) => {
            if (!isObject(item)) return;

            const associations = item.associations;
            if (!isObject(associations)) return;

            const docIds = associations.loanDocuments;
            if (!isArray(docIds)) return;

            if (docIds.includes(loanDocumentId)) {
              pushResult(groupKey, item);
            }
          });
        };

        // Arrays of associatables on root
        scanAssociatableArray("borrowers", loanData.borrowers);
        scanAssociatableArray("subjectProperties", loanData.subjectProperties);
        scanAssociatableArray("entities", loanData.entities);
        scanAssociatableArray("comments", loanData.comments);

        // Optional single-object associatable (your current borrowerEntity shape)
        const scanAssociatableSingle = (groupKey, item) => {
          if (!isObject(item)) return;

          const associations = item.associations;
          if (!isObject(associations)) return;

          const docIds = associations.loanDocuments;
          if (!isArray(docIds)) return;

          if (docIds.includes(loanDocumentId)) {
            pushResult(groupKey, item);
          }
        };

        scanAssociatableSingle("borrowerEntity", loanData.borrowerEntity);

        return { totalCount, categorized };
      } catch (error) {
        return { totalCount: 0, categorized: {} };
      }
    },
    []
  );

  // const getLoanDocumentAssociationSummary = useCallback(
  //   (loanData, loanDocumentId) => {
  //     try {
  //       if (!loanDocumentId || !isObject(loanData)) {
  //         return { totalCount: 0, categorizedIds: {} };
  //       }

  //       const categorizedIds = {};
  //       let totalCount = 0;

  //       const scanAssociatableArray = (groupKey, arr) => {
  //         if (!isArray(arr)) return;

  //         arr.forEach((item) => {
  //           if (!isObject(item)) return;

  //           const associations = item.associations;
  //           if (!isObject(associations)) return;

  //           const docIds = associations.loanDocuments;
  //           if (!isArray(docIds)) return;

  //           if (docIds.includes(loanDocumentId)) {
  //             const id = item._id;
  //             if (!id) return;

  //             if (!categorizedIds[groupKey]) categorizedIds[groupKey] = [];
  //             categorizedIds[groupKey].push(id);
  //             totalCount += 1;
  //           }
  //         });
  //       };

  //       scanAssociatableArray("borrowers", loanData.borrowers);
  //       scanAssociatableArray("subjectProperties", loanData.subjectProperties);
  //       scanAssociatableArray("entities", loanData.entities);

  //       const scanAssociatableSingle = (groupKey, item) => {
  //         if (!isObject(item)) return;

  //         const associations = item.associations;
  //         if (!isObject(associations)) return;

  //         const docIds = associations.loanDocuments;
  //         if (!isArray(docIds)) return;

  //         if (docIds.includes(loanDocumentId)) {
  //           const id = item._id;
  //           if (!id) return;

  //           if (!categorizedIds[groupKey]) categorizedIds[groupKey] = [];
  //           categorizedIds[groupKey].push(id);
  //           totalCount += 1;
  //         }
  //       };

  //       scanAssociatableSingle("borrowerEntity", loanData.borrowerEntity);

  //       return { totalCount, categorizedIds };
  //     } catch (error) {
  //       return { totalCount: 0, categorizedIds: {} };
  //     }
  //   },
  //   []
  // );

  /**
   * Returns the list of associatable objects for a specified key, e.g.:
   * - "borrowers"
   * - "subjectProperties"
   * - "entities"
   *
   * Safe defaults:
   * - returns [] when the key is missing or not an array
   */
  const getAssociatableObjects = useCallback((loanData, associatableKey) => {
    try {
      if (!isObject(loanData) || !associatableKey) return [];

      const value = loanData[associatableKey];
      if (!isArray(value)) return [];

      // Only keep object-like rows (guards against malformed arrays)
      return value.filter((item) => isObject(item) && !isArray(item));
    } catch (error) {
      return [];
    }
  }, []);

  const getAssociatableObjectsByLoanDocumentId = useCallback(
    (loanData, associatableKey, loanDocumentId) => {
      try {
        if (!isObject(loanData) || !associatableKey || !loanDocumentId)
          return [];

        const arr = loanData[associatableKey];
        if (!isArray(arr)) return [];

        return arr.filter((item) => {
          if (!isObject(item)) return false;

          const associations = item.associations;
          if (!isObject(associations)) return false;

          const docIds = associations.loanDocuments;
          if (!isArray(docIds)) return false;

          return docIds.includes(loanDocumentId);
        });
      } catch (error) {
        return [];
      }
    },
    []
  );

  const getAssociationDocumentsByBorrowerId = useCallback(
    (loanData, borrowerId) => {
      const loanDocuments = loanData?.loanDocuments;
      const borrower = loanData?.borrowers?.find(
        (borrower) => borrower?._id === borrowerId
      );
      const borrowerAssociations = borrower?.associations;

      const borrowerAssociationIds = borrowerAssociations?.loanDocuments;
      const borrowerFiles = loanDocuments?.filter((doc) =>
        borrowerAssociationIds?.includes(doc._id)
      );

      return borrowerFiles || [];
    },
    []
  );

  const getAssociationDocumentsByPropertyId = useCallback(
    (loanData, propertyId) => {
      const loanDocuments = loanData?.loanDocuments;
      const property = loanData?.subjectProperties?.find(
        (p) => p?._id === propertyId,
      );
      const propertyAssociationIds = property?.associations?.loanDocuments;
      const propertyFiles = loanDocuments?.filter((doc) =>
        propertyAssociationIds?.includes(doc._id),
      );

      return propertyFiles || [];
    },
    [],
  );

  return {
    getTotalArrayItems,
    getLoanDocumentAssociationSummary,
    getAssociatableObjects,
    getAssociatableObjectsByLoanDocumentId,
    getAssociationDocumentsByBorrowerId,
    getAssociationDocumentsByPropertyId,
  };
};

export default useAssociationUtils;
